import { HumanInterrupt } from '@langchain/langgraph/prebuilt'
import { t } from '@lingui/macro'
import { useState } from 'react'
import useInterruptedActions from '../../../hooks/UseInterruptedActions'
import { cn } from '../../../lib/utils'
import { Button } from '../../ui/button'
import { InboxItemInput } from './InboxItemInput'
import { ThreadIdCopyable } from './ThreadId'

interface ThreadActionsViewProps {
    interrupt: HumanInterrupt
    handleShowSidePanel: (showState: boolean, showDescription: boolean) => void
    showState: boolean
    showDescription: boolean
}

function ButtonGroup({
    handleShowState,
    handleShowDescription,
    showingState,
    showingDescription
}: {
    handleShowState: () => void
    handleShowDescription: () => void
    showingState: boolean
    showingDescription: boolean
}) {
    return (
        <div className='flex flex-row items-center justify-center gap-0'>
            <Button
                variant='outline'
                className={cn('rounded-l-md rounded-r-none border-r-[0px]', showingState ? 'text-black' : 'bg-white')}
                size='sm'
                onClick={handleShowState}
            >
                {t`State`}
            </Button>
            <Button
                variant='outline'
                className={cn('rounded-l-none rounded-r-md border-l-[0px]', showingDescription ? 'text-black' : 'bg-white')}
                size='sm'
                onClick={handleShowDescription}
            >
                {t`Description`}
            </Button>
        </div>
    )
}

export function ThreadActionsView({ interrupt, handleShowSidePanel, showDescription, showState }: ThreadActionsViewProps) {
    const [threadId] = useState<string | null>(null)

    const {
        acceptAllowed,
        hasEdited,
        hasAddedResponse,
        streaming,
        supportsMultipleMethods,
        streamFinished,
        loading,
        handleSubmit,
        handleIgnore,
        handleResolve,
        setSelectedSubmitType,
        setHasAddedResponse,
        setHasEdited,
        humanResponse,
        setHumanResponse,
        initialHumanInterruptEditValue
    } = useInterruptedActions({
        interrupt
    })

    const threadTitle = interrupt.action_request.action || t`Unknown`
    const actionsDisabled = loading || streaming
    const ignoreAllowed = interrupt.config.allow_ignore

    return (
        <div className='flex min-h-full w-full flex-col gap-9'>
            {/* Header */}
            <div className='flex w-full flex-wrap items-center justify-between gap-3'>
                <div className='flex items-center justify-start gap-3'>
                    <p className='text-2xl tracking-tighter text-pretty'>{threadTitle}</p>
                    {threadId && <ThreadIdCopyable threadId={threadId} />}
                </div>
                <div className='flex flex-row items-center justify-start gap-2'>
                    <ButtonGroup
                        handleShowState={() => handleShowSidePanel(true, false)}
                        handleShowDescription={() => handleShowSidePanel(false, true)}
                        showingState={showState}
                        showingDescription={showDescription}
                    />
                </div>
            </div>

            <div className='flex w-full flex-row items-center justify-start gap-2'>
                <Button
                    variant='outline'
                    className='border-gray-500 bg-white font-normal text-gray-800'
                    onClick={handleResolve}
                    disabled={actionsDisabled}
                >
                    {t`Mark as Resolved`}
                </Button>
                {ignoreAllowed && (
                    <Button
                        variant='outline'
                        className='border-gray-500 bg-white font-normal text-gray-800'
                        onClick={handleIgnore}
                        disabled={actionsDisabled}
                    >
                        {t`Ignore`}
                    </Button>
                )}
            </div>

            {/* Actions */}
            <InboxItemInput
                acceptAllowed={acceptAllowed}
                hasEdited={hasEdited}
                hasAddedResponse={hasAddedResponse}
                interruptValue={interrupt}
                humanResponse={humanResponse}
                initialValues={initialHumanInterruptEditValue.current}
                setHumanResponse={setHumanResponse}
                streaming={streaming}
                streamFinished={streamFinished}
                supportsMultipleMethods={supportsMultipleMethods}
                setSelectedSubmitType={setSelectedSubmitType}
                setHasAddedResponse={setHasAddedResponse}
                setHasEdited={setHasEdited}
                handleSubmit={handleSubmit}
            />
        </div>
    )
}
